import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { AnimatePresence, motion } from "framer-motion";
import {
    ArrowRight,
    Cpu,
    Pencil,
    X
} from "lucide-react";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

interface PromptsIntroCardProps {
    onCreateWritingAction: () => void;
    onCreateSystemInstruction: () => void;
}

export function PromptsIntroCard({ onCreateWritingAction, onCreateSystemInstruction }: PromptsIntroCardProps) {
    const { projectId } = useParams();
    const [isVisible, setIsVisible] = useState(true);
    const [activeId, setActiveId] = useState<string>('writing');

    useEffect(() => {
        if (projectId) {
            const isClosed = localStorage.getItem(`prompts_intro_card_closed_${projectId}`);
            if (isClosed === 'true') {
                setIsVisible(false);
            }
        }
    }, [projectId]);

    const dismiss = () => {
        if (projectId) {
            localStorage.setItem(`prompts_intro_card_closed_${projectId}`, 'true');
            setIsVisible(false);
        }
    };

    if (!isVisible) return null;

    const modules = [
        {
            id: 'writing',
            title: 'Writing Actions',
            subtitle: "Transform your text",
            description: "Define custom commands to rewrite, shorten, expand, or change the tone of your selected text directly in the editor.",
            icon: Pencil,
            actionLabel: "Create Action",
            action: onCreateWritingAction,
            tags: ["Rewrite", "Tone", "Edit"]
        },
        {
            id: 'system',
            title: 'System Instructions',
            subtitle: "Shape the Persona",
            description: "Set global behavior rules. Define how the AI should talk, what it knows, and how it should assist you throughout the project.",
            icon: Cpu,
            actionLabel: "Create Instruction",
            action: onCreateSystemInstruction,
            tags: ["Persona", "Behavior", "Context"]
        }
    ];

    const activeModule = modules.find(m => m.id === activeId) || modules[0];

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-8"
        >
            <Card className="relative overflow-hidden border-border bg-card shadow-sm group/container">
                {/* Unified Glass Background */}
                <div className="absolute inset-0 bg-gradient-to-br from-background via-muted/20 to-background/50 pointer-events-none" />

                {/* Dismiss Button - Top Right */}
                <div className="absolute top-2 right-2 z-20">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => dismiss()}
                        className="h-8 w-8 text-muted-foreground/50 hover:text-foreground hover:bg-background/80 rounded-full transition-colors"
                        title="Dismiss guide"
                    >
                        <X className="h-4 w-4" />
                    </Button>
                </div>

                <CardContent className="relative p-0 grid grid-cols-1 lg:grid-cols-12 min-h-[320px]">

                    {/* Left Column: Navigation List */}
                    <div className="lg:col-span-3 p-4 border-b lg:border-b-0 lg:border-r border-border/40 flex flex-col justify-between bg-muted/5">
                        <div className="h-full flex flex-col">
                            <div className="flex items-center justify-between mb-4 px-3 pt-2">
                                <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground/50">
                                    Get Started
                                </h3>
                            </div>

                            <div className="space-y-1 flex-1">
                                {modules.map((module) => (
                                    <button
                                        key={module.id}
                                        onMouseEnter={() => setActiveId(module.id)}
                                        onClick={() => setActiveId(module.id)}
                                        className={cn(
                                            "w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-all duration-200 outline-none focus-visible:ring-2",
                                            activeId === module.id
                                                ? "bg-background shadow-sm ring-1 ring-border/50"
                                                : "hover:bg-background/40 text-muted-foreground hover:text-foreground"
                                        )}
                                    >
                                        <div className={cn(
                                            "h-7 w-7 rounded-md flex items-center justify-center transition-colors shrink-0",
                                            activeId === module.id ? "bg-primary/10 text-primary" : "bg-muted/20 text-muted-foreground"
                                        )}>
                                            <module.icon className="h-3.5 w-3.5" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className={cn("font-medium text-sm truncate", activeId === module.id ? "text-foreground" : "text-muted-foreground")}>
                                                {module.title}
                                            </div>
                                        </div>
                                        {activeId === module.id && (
                                            <motion.div layoutId="active-indicator" className="ml-auto shrink-0">
                                                <ArrowRight className="h-3.5 w-3.5 text-primary" />
                                            </motion.div>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Right Column: Dynamic Preview */}
                    <div className="lg:col-span-9 p-8 relative flex flex-col justify-between">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={activeModule.id}
                                initial={{ opacity: 0, x: 10 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -10 }}
                                transition={{ duration: 0.2 }}
                                className="h-full flex flex-col justify-between"
                            >
                                <div className="space-y-4">
                                    <h2 className="text-3xl font-bold tracking-tight text-foreground">
                                        {activeModule.subtitle}
                                    </h2>
                                    <p className="text-muted-foreground text-lg leading-relaxed max-w-xl">
                                        {activeModule.description}
                                    </p>
                                </div>

                                <div className="pt-4">
                                    <Button
                                        onClick={activeModule.action}
                                        className="rounded-full px-6 h-10 gap-2 font-medium shadow-lg shadow-primary/10 hover:shadow-primary/20 transition-all"
                                    >
                                        <activeModule.icon className="h-4 w-4" />
                                        {activeModule.actionLabel}
                                    </Button>

                                </div>
                            </motion.div>
                        </AnimatePresence>

                        {/* Background Decoration */}
                        <div className="absolute right-0 top-0 p-32 bg-gradient-to-b from-primary/5 to-transparent rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none opacity-50" />
                    </div>
                </CardContent>
            </Card>
        </motion.div>
    );
}
